import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Button, Badge } from '../ui';
import {
  Check, ChevronRight, ChevronDown, ChevronUp, Facebook, DollarSign,
  Layers, FileText, FlaskConical, X, Minimize2, Maximize2, SkipForward,
  Sparkles, ExternalLink, ShieldCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';

const ONBOARDING_STEPS = [
  { 
    id: 'connect', 
    title: 'Connect Ad Accounts', 
    description: 'Link your ad platforms',
    subSteps: [
      { id: 'meta_ads',   name: 'Meta Ads',    path: '/meta-ads',   icon: Facebook,    color: 'bg-blue-500'   },
      { id: 'google_ads', name: 'Google Ads',  path: '/google-ads', icon: DollarSign,  color: 'bg-red-500'    },
      { id: 'adsense',    name: 'AdSense',     path: '/adsense',    icon: DollarSign,  color: 'bg-green-500'  },
      { id: 'admanager',  name: 'Ad Manager',  path: '/ad-manager', icon: Layers,      color: 'bg-purple-500' },
      { id: 'hapiguard',  name: 'HapiGuard',   path: '/hapiguard',  icon: ShieldCheck, color: 'bg-emerald-600'},
    ]
  },
  { 
    id: 'landing_page', 
    title: 'Create Landing Page', 
    description: 'Build your first page',
    path: '/landing-pages',
    icon: FileText,
  },
  { 
    id: 'ab_test', 
    title: 'Set Up A/B Test', 
    description: 'Start optimizing',
    path: '/ab-tests',
    icon: FlaskConical,
  },
];

export default function OnboardingWidget() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Load from localStorage
  useEffect(() => {
    const savedMinimized = localStorage.getItem('onboarding_minimized');
    if (savedMinimized === 'true') setMinimized(true);
  }, []);

  // Load onboarding progress
  const loadProgress = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('onboarding_progress')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Progress load error:', error);
      }

      let progressData = data || {};

      // Auto-detect: if user has a running test but onboarding doesn't know yet, fix it
      if (!progressData.first_ab_test_created) {
        const { count } = await supabase
          .from('ab_tests')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .in('status', ['running', 'active', 'paused', 'completed']);
        if (count && count > 0) {
          await supabase.from('onboarding_progress')
            .update({ first_ab_test_created: true, updated_at: new Date().toISOString() })
            .eq('user_id', user.id);
          progressData = { ...progressData, first_ab_test_created: true };
        }
      }

      // Auto-detect: if user has a published landing page but onboarding doesn't know yet
      if (!progressData.first_landing_page_created) {
        const { count } = await supabase
          .from('landing_pages')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);
        if (count && count > 0) {
          await supabase.from('onboarding_progress')
            .update({ first_landing_page_created: true, updated_at: new Date().toISOString() })
            .eq('user_id', user.id);
          progressData = { ...progressData, first_landing_page_created: true };
        }
      }

      if (data) {
        setProgress(progressData);
      } else {
        // Create progress record if doesn't exist
        const { data: newProgress } = await supabase
          .from('onboarding_progress')
          .upsert({ user_id: user.id }, { onConflict: 'user_id' })
          .select()
          .single();
        setProgress(newProgress || progressData);
      }
    } catch (e) {
      console.error('Load progress error:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Refresh progress periodically (every 5 seconds when not minimized)
  useEffect(() => {
    if (minimized || !user) return;
    
    const interval = setInterval(loadProgress, 5000);
    return () => clearInterval(interval);
  }, [minimized, user, loadProgress]);

  // Don't show if onboarding completed or no user
  if (!user || !profile || profile.onboarding_completed || loading) {
    return null;
  }

  // Don't show on onboarding page itself
  if (location.pathname === '/onboarding') {
    return null;
  }

  const handleMinimize = () => {
    setMinimized(true);
    localStorage.setItem('onboarding_minimized', 'true');
  };

  const handleMaximize = () => {
    setMinimized(false);
    localStorage.setItem('onboarding_minimized', 'false');
  };

  const handleSkipStep = async (stepId) => {
    try {
      const updateData = { updated_at: new Date().toISOString() };
      
      if (stepId === 'meta_ads')        updateData.meta_ads_connected = true;
      else if (stepId === 'google_ads')  updateData.google_ads_connected = true;
      else if (stepId === 'adsense')     updateData.adsense_connected = true;
      else if (stepId === 'admanager')   updateData.admanager_connected = true;
      else if (stepId === 'hapiguard')   updateData.hapiguard_connected = true;
      else if (stepId === 'landing_page') updateData.first_landing_page_created = true;
      else if (stepId === 'ab_test')     updateData.first_ab_test_created = true;

      await supabase
        .from('onboarding_progress')
        .update(updateData)
        .eq('user_id', user.id);

      toast.success('Step skipped');
      loadProgress();
    } catch (e) {
      console.error('Skip step error:', e);
    }
  };

  const handleEndOnboarding = async () => {
    try {
      await supabase
        .from('onboarding_progress')
        .update({ completed_at: new Date().toISOString() })
        .eq('user_id', user.id);

      await supabase
        .from('profiles')
        .update({ onboarding_completed: true })
        .eq('id', user.id);

      await refreshProfile();
      toast.success('Onboarding completed! 🎉');
    } catch (e) {
      console.error('End onboarding error:', e);
    }
  };

  const handleConnectPlatform = (path) => {
    navigate(path);
  };

  // Calculate completion
  const connectSteps = ONBOARDING_STEPS[0].subSteps;
  const connectedCount = connectSteps.filter(s => {
    if (s.id === 'meta_ads')   return progress?.meta_ads_connected;
    if (s.id === 'google_ads') return progress?.google_ads_connected;
    if (s.id === 'adsense')    return progress?.adsense_connected;
    if (s.id === 'admanager')  return progress?.admanager_connected;
    if (s.id === 'hapiguard')  return progress?.hapiguard_connected;
    return false;
  }).length;

  const hasAnyConnection = connectedCount > 0;
  const hasLandingPage = progress?.first_landing_page_created;
  const hasAbTest = progress?.first_ab_test_created;
  
  const totalSteps = 3;
  const completedSteps = (hasAnyConnection ? 1 : 0) + (hasLandingPage ? 1 : 0) + (hasAbTest ? 1 : 0);
  const progressPercent = Math.round((completedSteps / totalSteps) * 100);

  // All steps complete
  if (completedSteps === totalSteps) {
    return (
      <div className="fixed bottom-4 right-4 z-50 bg-background border rounded-lg shadow-lg p-4 max-w-sm">
        <div className="flex items-center gap-3 mb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold">Setup Complete!</p>
            <p className="text-sm text-muted-foreground">You're ready to go</p>
          </div>
        </div>
        <Button onClick={handleEndOnboarding} className="w-full">
          Finish Setup
        </Button>
      </div>
    );
  }

  // Minimized state
  if (minimized) {
    return (
      <button
        onClick={handleMaximize}
        className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full shadow-lg hover:bg-primary/90 transition-colors"
      >
        <Sparkles className="h-4 w-4" />
        <span className="text-sm font-medium">Setup: {progressPercent}%</span>
        <Maximize2 className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 bg-background border rounded-lg shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-primary/5 border-b p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">Getting Started</span>
          <Badge variant="secondary" className="text-xs">{progressPercent}%</Badge>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleMinimize}
            className="p-1 hover:bg-muted rounded"
            title="Minimize"
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button
            onClick={handleEndOnboarding}
            className="p-1 hover:bg-muted rounded"
            title="Skip onboarding"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-muted">
        <div 
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      {/* Content */}
      <div className="p-3 max-h-96 overflow-y-auto">
        {/* Step 1: Connect Accounts */}
        <div className="mb-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between p-2 rounded hover:bg-muted/50"
          >
            <div className="flex items-center gap-2">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                hasAnyConnection ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {hasAnyConnection ? <Check className="h-3.5 w-3.5" /> : '1'}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Connect Ad Accounts</p>
                <p className="text-xs text-muted-foreground">{connectedCount}/{connectSteps.length} connected</p>
              </div>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>

          {expanded && (
            <div className="ml-8 mt-2 space-y-2">
              {connectSteps.map((platform) => {
                const isConnected = 
                  (platform.id === 'meta_ads' && progress?.meta_ads_connected) ||
                  (platform.id === 'google_ads' && progress?.google_ads_connected) ||
                  (platform.id === 'adsense' && progress?.adsense_connected) ||
                  (platform.id === 'admanager' && progress?.admanager_connected);

                return (
                  <div key={platform.id} className="flex items-center justify-between p-2 rounded bg-muted/30">
                    <div className="flex items-center gap-2">
                      <div className={`p-1 rounded ${platform.color}`}>
                        <platform.icon className="h-3 w-3 text-white" />
                      </div>
                      <span className="text-sm">{platform.name}</span>
                      {isConnected && <Check className="h-3.5 w-3.5 text-primary" />}
                    </div>
                    {!isConnected && (
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => handleSkipStep(platform.id)}
                        >
                          Skip
                        </Button>
                        <Button
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => handleConnectPlatform(platform.path)}
                        >
                          Connect
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Step 2: Landing Page */}
        <div className="mb-3">
          <div className="flex items-center justify-between p-2 rounded hover:bg-muted/50">
            <div className="flex items-center gap-2">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                hasLandingPage ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {hasLandingPage ? <Check className="h-3.5 w-3.5" /> : '2'}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Create Landing Page</p>
                <p className="text-xs text-muted-foreground">Build your first page</p>
              </div>
            </div>
            {!hasLandingPage && (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={() => handleSkipStep('landing_page')}
                >
                  Skip
                </Button>
                <Button
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => navigate('/landing-pages')}
                >
                  Create
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Step 3: A/B Test */}
        <div className="mb-3">
          <div className="flex items-center justify-between p-2 rounded hover:bg-muted/50">
            <div className="flex items-center gap-2">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                hasAbTest ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {hasAbTest ? <Check className="h-3.5 w-3.5" /> : '3'}
              </div>
              <div className="text-left">
                <p className="text-sm font-medium">Set Up A/B Test</p>
                <p className="text-xs text-muted-foreground">Start optimizing</p>
              </div>
            </div>
            {!hasAbTest && (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  onClick={() => handleSkipStep('ab_test')}
                >
                  Skip
                </Button>
                <Button
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => navigate('/ab-tests')}
                >
                  Create
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t p-2 bg-muted/30">
        <button
          onClick={handleEndOnboarding}
          className="w-full text-center text-xs text-muted-foreground hover:text-foreground py-1"
        >
          Skip setup and explore on my own
        </button>
      </div>
    </div>
  );
}
